import { useState, useEffect, useCallback } from 'react';
import {
    LayoutDashboard, Users, Wrench, FileText, Receipt,
    BarChart3, Sparkles, Settings, Bell, Menu, X, LogOut, Undo2, Landmark, CalendarDays,
    LifeBuoy
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useVertical } from '../../context/VerticalContext';
import { useRole } from '../../context/RoleContext';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationBell } from '../ui/NotificationBell';
import { NotificationPanel } from '../ui/NotificationPanel';
import { supabase } from '../../lib/supabase';

const iconMap: Record<string, any> = {
    LayoutDashboard,
    Wrench,
    Users,
    FileText,
    Receipt,
    BarChart3,
    Sparkles,
    Settings,
    Undo2,
    Landmark,
    CalendarDays,
};

export const MobileNav = () => {
    const { molded } = useVertical();
    const { can } = useRole();
    const { unreadCount, pendingCreditsCount } = useNotifications();
    const location = useLocation();
    const navigate = useNavigate();
    const activePath = location.pathname;

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);

    // Close drawer on route change
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    // Close drawer on Escape key
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setDrawerOpen(false);
        };
        if (drawerOpen) {
            document.addEventListener('keydown', onKey);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [drawerOpen]);

    const toggleDrawer = useCallback(() => setDrawerOpen(prev => !prev), []);

    const sidebarConfig = molded.sidebar_config || [];

    // Schedule (full calendar) — its own top-level page, injected after Dashboard.
    const withSchedule = [...sidebarConfig];
    if (!withSchedule.some((i) => i.path === '/schedule')) {
        const dashIdx = withSchedule.findIndex((i) => i.path === '/');
        withSchedule.splice(dashIdx >= 0 ? dashIdx + 1 : 0, 0, {
            label: 'Schedule',
            path: '/schedule',
            icon: 'CalendarDays',
            permission: 'workshop',
        } as (typeof sidebarConfig)[number]);
    }

    const visibleNavItems = withSchedule.filter((item) =>
        !item.permission || can(item.permission as any)
    );

    return (
        <>
            {/* ─── Bottom Trigger Bar ─── */}
            <nav data-theme="dark" className="fixed bottom-0 left-0 right-0 h-[calc(4rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)] bg-sunken/95 backdrop-blur-md border-t border-line flex items-center justify-between px-4 lg:hidden z-50 print:hidden no-print">
                <button
                    onClick={toggleDrawer}
                    className="flex items-center gap-1.5 px-2.5 h-10 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors active:scale-95"
                    aria-label="Open navigation menu"
                >
                    <Menu className="w-5 h-5 shrink-0" />
                    <span className="text-xs font-medium tracking-wide">Menu</span>
                </button>

                <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <img src="/logo.webp" alt="" className="h-7 w-auto" />
                    <span className="text-[17px] font-semibold tracking-[-0.03em] font-display text-fg">
                        ForgeDash
                    </span>
                </Link>

                <button
                    onClick={() => setNotificationsOpen(true)}
                    aria-label="Notifications"
                    className="relative flex items-center justify-center w-12 h-12 rounded-lg text-fg-3 hover:text-fg transition-colors active:scale-95"
                >
                    <Bell className="w-6 h-6" />
                    {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center bg-danger-fill text-fg text-fd-3xs font-bold rounded-full">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>
            </nav>

            {/* Same notifications panel the sidebar bell opens. */}
            <NotificationPanel isOpen={notificationsOpen} onClose={() => setNotificationsOpen(false)} />

            {/* ─── Backdrop Overlay ─── */}
            <div
                className={`fixed inset-0 bg-scrim/70 z-[60] lg:hidden transition-opacity duration-300 ${drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={() => setDrawerOpen(false)}
                aria-hidden="true"
            />

            {/* ─── Side Drawer ─── */}
            <aside data-theme="dark"
                className={`fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-sunken border-r border-line shadow-lifted z-[70] lg:hidden flex flex-col transition-transform duration-300 ease-in-out print:hidden no-print ${drawerOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                {/* Drawer Header */}
                <div className="px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                        <img src="/logo.webp" alt="" className="h-8 w-auto" />
                        <span className="text-[19px] font-semibold tracking-[-0.03em] font-display text-fg">
                            ForgeDash
                        </span>
                    </Link>
                    <button
                        onClick={() => setDrawerOpen(false)}
                        className="flex items-center justify-center w-10 h-10 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors"
                        aria-label="Close navigation menu"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 flex items-center gap-2 mb-4">
                    <p className="fd-eyebrow">
                        {molded.jargon.trade_label} OS
                    </p>
                </div>

                {/* Nav Items */}
                <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
                    {visibleNavItems.map((item) => {
                        const Icon = iconMap[item.icon] || LayoutDashboard;
                        const isActive = item.path === '/'
                            ? activePath === '/'
                            : activePath.startsWith(item.path);

                        return (
                            <Link
                                key={item.label}
                                to={item.path}
                                className={`
                                    relative flex items-center gap-3 pl-4 pr-3 h-[46px] rounded-[var(--fd-r-md)]
                                    transition-colors duration-150 group
                                    ${isActive
                                        ? 'bg-raised text-fg shadow-[inset_0_0_0_1px_var(--fd-border)]'
                                        : 'text-fg-3 hover:text-fg hover:bg-card'
                                    }
                                `}
                            >
                                {isActive && (
                                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-[22px] w-[3px] rounded-r bg-gold-fill" />
                                )}
                                <Icon
                                    strokeWidth={2}
                                    className={`w-5 h-5 shrink-0 transition-colors ${isActive ? 'text-gold' : 'text-fg-3 group-hover:text-fg'}`}
                                />
                                <span className={`flex-1 text-sm ${isActive ? 'font-semibold' : 'font-normal'}`}>
                                    {item.label}
                                </span>
                                {item.path === '/returns' && pendingCreditsCount > 0 && (
                                    <span className="ml-auto bg-danger-fill text-fg py-0.5 px-2 rounded-full text-fd-3xs font-bold font-mono">
                                        {pendingCreditsCount > 99 ? '99+' : pendingCreditsCount}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Drawer Footer */}
                <div className="px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 border-t border-line space-y-1">
                    <Link
                        to="/help"
                        className={`flex items-center gap-3 px-3 h-[44px] w-full rounded-lg transition-colors ${activePath.startsWith('/help')
                            ? 'text-fg bg-gold-fill/10'
                            : 'text-fg-3 hover:text-fg hover:bg-card'
                            }`}
                    >
                        <LifeBuoy strokeWidth={2} className={`w-4 h-4 ${activePath.startsWith('/help') ? 'text-gold' : ''}`} />
                        <span className="text-fd-sm-tight">Help &amp; Guides</span>
                    </Link>
                    <div className="px-3 flex items-center justify-end">
                        <NotificationBell />
                    </div>
                    <button
                        onClick={async () => {
                            await supabase.auth.signOut();
                            navigate('/');
                        }}
                        className="flex items-center gap-3 px-3 h-[44px] w-full rounded-[var(--fd-r-md)] text-fg-3 hover:text-danger hover:bg-card transition-colors"
                    >
                        <LogOut strokeWidth={2} className="w-4 h-4" />
                        <span className="text-fd-sm-tight">Sign out</span>
                    </button>
                </div>
            </aside>
        </>
    );
};
